import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleHalfStroke,
  faCircleXmark,
  faCircleQuestion,
  faTriangleExclamation,
  faUserGraduate,
  faCircleInfo,
  faCommentDots,
} from '@fortawesome/free-solid-svg-icons';
import './Evaluation.css';

const API = process.env.REACT_APP_API_BASE_URL;

// Same payment codes as before
const PAY = {
  0: { label: 'Full pay', tone: 'full', icon: faCircleCheck },
  1: { label: 'Half pay', tone: 'half', icon: faCircleHalfStroke },
  2: { label: 'No pay', tone: 'none', icon: faCircleXmark },
};
const payOf = (v) => PAY[v] || { label: 'Not set', tone: 'unknown', icon: faCircleQuestion };

// Same colour rules as before
const TEACHER_TARGET = 50; // minutes
const STUDENT_TARGET = 30;

const durationTone = (seconds, who) => {
  const min = (seconds || 0) / 60;
  if (!min) return 'zero';
  if (who === 'teacher') {
    if (min >= TEACHER_TARGET) return 'good';
    if (min >= 30) return 'warn';
    return 'bad';
  }
  return min >= STUDENT_TARGET ? 'good' : 'warn';
};

const formatDuration = (seconds) => {
  if (!seconds) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

const dayLabel = (d) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(d) === dayKey(today)) return 'Today';
  if (dayKey(d) === dayKey(yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
};

const RULES = [
  { tone: 'full', label: 'Full pay', text: 'Teacher 50+ min and student 30+ min' },
  { tone: 'half', label: 'Half pay', text: 'Teacher 50+ min and student under 30 · Teacher 30–50 min and student 30+ · Teacher 30+ min and student didn’t join' },
  { tone: 'none', label: 'No pay', text: 'Teacher under 30 min' },
];

/* Small ring that fills up to the target time */
const Ring = ({ who, seconds }) => {
  const target = who === 'teacher' ? TEACHER_TARGET : STUDENT_TARGET;
  const ratio = Math.min(1, ((seconds || 0) / 60) / target);
  const r = 15;
  const c = 2 * Math.PI * r;
  return (
    <div className={`rmaev-time is-${durationTone(seconds, who)}`}>
      <svg viewBox="0 0 36 36" aria-hidden="true">
        <circle className="rmaev-ring-bg" cx="18" cy="18" r={r} />
        <circle
          className="rmaev-ring"
          cx="18"
          cy="18"
          r={r}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - ratio)}
        />
      </svg>
      <div>
        <span className="rmaev-time-label">{who === 'teacher' ? 'Teacher' : 'Student'}</span>
        <strong>{formatDuration(seconds)}</strong>
      </div>
    </div>
  );
};

const Photo = ({ src }) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className="rmaev-photo">
      {src && !broken ? (
        <img src={src} alt="" onError={() => setBroken(true)} />
      ) : (
        <FontAwesomeIcon icon={faUserGraduate} />
      )}
    </span>
  );
};

const Evaluation = ({ userData }) => {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [showRules, setShowRules] = useState(false);
  const [reload, setReload] = useState(0);
  const [teacher, setTeacher] = useState('all');

  const adminId = userData?.id;

  // ---------- Load (same endpoint + same token header) ----------
  useEffect(() => {
    if (!adminId) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        // same endpoint as before: all teachers, last 5 days
        const res = await axios.get(`${API}/api/admin/adminfeedback/evaluations`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` },
        });
        if (cancelled) return;
        const list = (res.data?.evaluations || [])
          .map((e) => ({ ...e, date: new Date(e.ClassDateTime), teacherName: e.TeacherName || 'Unknown teacher' }))
          .sort((a, b) => b.date - a.date); // newest first
        setEvaluations(list);
        setError('');
      } catch (err) {
        console.error('Error fetching evaluations:', err);
        if (!cancelled) setError('We couldn’t load the evaluations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminId, reload]);

  // teachers in this period, with how many classes need attention
  const teacherList = (() => {
    const map = {};
    evaluations.forEach((e) => {
      if (!map[e.teacherName]) map[e.teacherName] = { name: e.teacherName, total: 0, attention: 0 };
      map[e.teacherName].total += 1;
      if (e.calculatedFullpay !== 0) map[e.teacherName].attention += 1;
    });
    return Object.values(map).sort((a, b) => b.attention - a.attention || b.total - a.total);
  })();

  const scoped = teacher === 'all' ? evaluations : evaluations.filter((e) => e.teacherName === teacher);

  // Same rule as before (admin): calculatedFullpay not 0 = needs attention
  const attention = scoped.filter((e) => e.calculatedFullpay !== 0);
  const shown = filter === 'attention' ? attention : scoped;

  const totals = { full: 0, half: 0, none: 0, unknown: 0 };
  scoped.forEach((e) => {
    totals[payOf(e.fullpay).tone] += 1;
  });
  const total = scoped.length;

  // group by day
  const groups = [];
  shown.forEach((e) => {
    const valid = !Number.isNaN(e.date.getTime());
    const key = valid ? dayKey(e.date) : 'unknown';
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, label: valid ? dayLabel(e.date) : 'Unknown date', items: [] };
      groups.push(g);
    }
    g.items.push(e);
  });

  if (loading) {
    return (
      <div className="rmaev" aria-busy="true">
        <div className="rmaev-skel rmaev-skel--hero" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="rmaev-skel" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rmaev">
        <div className="rmaev-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error}</p>
          <button type="button" className="rmaev-btn" onClick={() => setReload((n) => n + 1)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rmaev">
      {/* ---------- Teachers ---------- */}
      {teacherList.length > 1 && (
        <div className="rmaev-teachers" role="radiogroup" aria-label="Teacher">
          <button type="button" role="radio" aria-checked={teacher === 'all'} className={teacher === 'all' ? 'is-on' : ''} onClick={() => setTeacher('all')}>
            All teachers
          </button>
          {teacherList.map((tc) => (
            <button
              key={tc.name}
              type="button"
              role="radio"
              aria-checked={teacher === tc.name}
              className={teacher === tc.name ? 'is-on' : ''}
              onClick={() => setTeacher(tc.name)}
            >
              {tc.name}
              <span className={tc.attention ? 'is-alert' : ''}>{tc.attention ? tc.attention : tc.total}</span>
            </button>
          ))}
        </div>
      )}

      {/* ---------- Summary ---------- */}
      <section className="rmaev-hero" aria-label="Summary of the last 5 days">
        <div className="rmaev-score">
          <strong>
            {totals.full}
            <span>/{total}</span>
          </strong>
          <p>classes on full pay · last 5 days{teacher !== 'all' ? ` · ${teacher}` : ''}</p>
        </div>

        <div className="rmaev-split">
          <div className="rmaev-bar" aria-hidden="true">
            {total > 0 &&
              ['full', 'half', 'none', 'unknown'].map(
                (t) => totals[t] > 0 && <span key={t} className={`is-${t}`} style={{ flexGrow: totals[t] }} />
              )}
          </div>
          <ul className="rmaev-legend">
            <li className="is-full">
              <i /> Full <b>{totals.full}</b>
            </li>
            <li className="is-half">
              <i /> Half <b>{totals.half}</b>
            </li>
            <li className="is-none">
              <i /> No pay <b>{totals.none}</b>
            </li>
            <li>
              <button
                type="button"
                className="rmaev-info"
                aria-expanded={showRules}
                onClick={() => setShowRules((v) => !v)}
              >
                <FontAwesomeIcon icon={faCircleInfo} /> How pay works
              </button>
            </li>
          </ul>
        </div>

        {showRules && (
          <dl className="rmaev-rules">
            {RULES.map((r) => (
              <div key={r.tone} className={`is-${r.tone}`}>
                <dt>{r.label}</dt>
                <dd>{r.text}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>

      {/* ---------- Filter ---------- */}
      {total > 0 && (
        <div className="rmaev-tabs" role="tablist" aria-label="Filter evaluations">
          <button type="button" role="tab" aria-selected={filter === 'all'} className={filter === 'all' ? 'is-on' : ''} onClick={() => setFilter('all')}>
            All <span>{total}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'attention'}
            className={filter === 'attention' ? 'is-on' : ''}
            onClick={() => setFilter('attention')}
          >
            Needs attention <span className={attention.length ? 'is-alert' : ''}>{attention.length}</span>
          </button>
        </div>
      )}

      {/* ---------- List ---------- */}
      {groups.length === 0 ? (
        <div className="rmaev-empty">
          <FontAwesomeIcon icon={faCircleCheck} className="is-done" />
          <p>{total === 0 ? 'No evaluations in the last 5 days.' : 'Every class is on full pay. Great work!'}</p>
        </div>
      ) : (
        groups.map((g) => (
          <section key={g.key} className="rmaev-day">
            <h3 className="rmaev-day-title">{g.label}</h3>
            <ul className="rmaev-list">
              {g.items.map((e) => {
                const pay = payOf(e.fullpay);
                const valid = !Number.isNaN(e.date.getTime());
                return (
                  <li key={e.Id} className="rmaev-row">
                    <div className="rmaev-student">
                      <Photo src={e.Image} />
                      <div className="rmaev-who">
                        <h4>{[e.FirstName, e.LastName].filter(Boolean).join(' ') || 'Student'}</h4>
                        <p>
                          {valid ? e.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                          {teacher === 'all' && <span className="rmaev-teacher"> · {e.teacherName}</span>}
                        </p>
                      </div>
                    </div>

                    <div className="rmaev-times">
                      <Ring who="teacher" seconds={e.TeacherDuration} />
                      <Ring who="student" seconds={e.StudentDuration} />
                    </div>

                    <span className={`rmaev-pay is-${pay.tone}`}>
                      <FontAwesomeIcon icon={pay.icon} />
                      {pay.label}
                    </span>

                    {e.AdminEvaluation && (
                      <p className="rmaev-note">
                        <FontAwesomeIcon icon={faCommentDots} />
                        <span>{e.AdminEvaluation}</span>
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </div>
  );
};

export default Evaluation;